import { ColorPaletteKind } from '@/core';
import { ColorBar } from '../../../components/ColorBar';
import { useSeriesColorGenerator } from '../../../components/context';
import { OptionsEditorControl } from '../OptionsEditorLayout';
import { SettingsAutocomplete, SettingsAutocompleteOption } from '../SettingsAutocomplete';

const DEFAULT_COLOR_PALETTE = 'Categorical';

export interface ColorPaletteSelectorProps {
  value: ColorPaletteKind;
  onChange: (colorPalette: ColorPaletteKind) => void;
}

export function ColorPaletteSelector({ value, onChange }: ColorPaletteSelectorProps) {
  const currentPalette: ColorPaletteKind = value ?? DEFAULT_COLOR_PALETTE;
  const { colorPaletteConfig, colorPaletteOptions } = useColorPaletteConfig();

  // Casting here to account for the fact that we don't expose the `CategoricalNoErrorColors` color
  // palette option in the options editor at this time.
  const currentPaletteConfig = colorPaletteConfig[currentPalette as keyof typeof colorPaletteConfig];

  const handleColorPaletteChange = (_: unknown, newPalette: SettingsAutocompleteOption): void => {
    onChange(newPalette.id as ColorPaletteKind);
  };

  return (
    <OptionsEditorControl
      label="Color palette"
      control={
        <SettingsAutocomplete
          value={{
            ...currentPaletteConfig,
            id: currentPalette,
          }}
          options={colorPaletteOptions}
          onChange={handleColorPaletteChange}
          disableClearable
        />
      }
    />
  );
}

function useColorPaletteConfig() {
  const colorGenerator = useSeriesColorGenerator();

  const colorPaletteConfig = {
    Categorical: {
      label: 'Classic',
      tooltipDescription:
        'The default Classic palette uses a set of twenty-four distinct colors optimized for readability.',
      description: ColorBar({ colors: colorGenerator.palette.Categorical }),
    },
    Consistent: {
      label: 'Consistent',
      tooltipDescription:
        'The Consistent palette allows you to assign the  same color consistently to a series of data, making it easier to correlate data across charts.',
      description: ColorBar({ colors: colorGenerator.palette.SpectrumConsistent }),
    },
    CategoricalNoStatusColors: {
      label: 'Status-neutral',
      tooltipDescription:
        'The Status-neutral palette excludes colors like red, orange, and green, providing an option that keeps visualizations clear and distinct from status indicators.',
      description: ColorBar({ colors: colorGenerator.palette.CategoricalNoStatusColors }),
    },
  };
  const colorPaletteOptions = Object.entries(colorPaletteConfig).map(([id, config]) => {
    return { ...config, id: id as ColorPaletteKind };
  });
  return { colorPaletteConfig, colorPaletteOptions };
}
